// Share dialog for a My List: turn link sharing on/off, copy the link, post it
// to social / text, and grab the PNG share card. The link only works while
// visibility is not "private"; the slug is minted by the backend the first time.
import { useEffect, useMemo, useState } from 'react';
import { MyListsService, getMyListErrorMessage } from '../../services/myListsService';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import styles from './ShareListModal.module.css';

export const VISIBILITY_OPTIONS = [
    { value: 'private', label: 'Private', hint: 'Only you can see this list.' },
    { value: 'public', label: 'Public', hint: 'Anyone can view this list from your profile or shared link.' },
];

export function buildShareLinks({ url, text }) {
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(text);
    return {
        x: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
        sms: `sms:?&body=${encodedText}%20${encodedUrl}`,
        email: `mailto:?subject=${encodedText}&body=${encodedText}%0A${encodedUrl}`,
    };
}

export default function ShareListModal({ isOpen, list, onClose, onListChange }) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [cardKey, setCardKey] = useState(0);

    const isShared = list?.visibility && list.visibility !== 'private';
    const shareUrl = isShared ? list.shareUrl : null;
    const shareText = useMemo(() => {
        if (!list) return '';
        const count = list.itemCount ?? 0;
        return `${list.name} — ${count} title${count === 1 ? '' : 's'} on my Aluvo list 🎬`;
    }, [list]);
    const links = useMemo(() => (shareUrl ? buildShareLinks({ url: shareUrl, text: shareText }) : null), [shareUrl, shareText]);
    const cardUrl = isShared && list.shareSlug ? `${MyListsService.cardUrl(list.shareSlug)}?v=${cardKey}` : null;

    useEffect(() => {
        if (!isOpen) return undefined;
        setError('');
        setCopied(false);
        setCardKey(Date.now());
        trackEvent(ANALYTICS_EVENTS.MY_LIST_SHARE_OPEN, { source_screen: 'my_list_detail', list_item_count: list?.itemCount ?? 0, visibility: list?.visibility });
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', handleKey);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose, list?.itemCount, list?.visibility]);

    if (!isOpen || !list) return null;

    async function changeVisibility(visibility) {
        if (visibility === list.visibility || saving) return;
        setSaving(true);
        setError('');
        try {
            const data = await MyListsService.setVisibility(list.id, visibility);
            const updated = data?.list ?? {};
            onListChange?.({ visibility: updated.visibility ?? visibility, shareSlug: updated.shareSlug ?? list.shareSlug ?? null, shareUrl: updated.shareUrl ?? null });
            trackEvent(ANALYTICS_EVENTS.MY_LIST_VISIBILITY_CHANGED, { previous_visibility: list.visibility, new_visibility: visibility, list_item_count: list.itemCount ?? 0, source_screen: 'share_dialog' });
        } catch (err) {
            setError(getMyListErrorMessage(err, "Couldn't update sharing. Please try again."));
        } finally {
            setSaving(false);
        }
    }

    async function copyLink() {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
        } catch {
            const input = document.createElement('input');
            input.value = shareUrl;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
        trackEvent(ANALYTICS_EVENTS.MY_LIST_SHARED, { channel: 'copy_link', list_item_count: list.itemCount ?? 0 });
    }

    async function nativeShare() {
        if (!shareUrl) return;
        if (navigator.share) {
            try {
                await navigator.share({ title: list.name, text: shareText, url: shareUrl });
                trackEvent(ANALYTICS_EVENTS.MY_LIST_SHARED, { channel: 'native', list_item_count: list.itemCount ?? 0 });
            } catch { /* user dismissed */ }
            return;
        }
        copyLink();
    }

    const track = (channel) => () => trackEvent(ANALYTICS_EVENTS.MY_LIST_SHARED, { channel, list_item_count: list.itemCount ?? 0 });

    return (
        <div className={styles.backdrop} onClick={saving ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="share-list-title">
            <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <div>
                        <div className={styles.eyebrow}>Share list</div>
                        <h2 id="share-list-title" className={styles.title}>{list.name}</h2>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
                </div>

                <fieldset className={styles.fieldset} disabled={saving}>
                    <legend className={styles.legend}>Visibility</legend>
                    {VISIBILITY_OPTIONS.map((option) => (
                        <label key={option.value} className={`${styles.option} ${list.visibility === option.value ? styles.optionActive : ''}`}>
                            <input
                                type="radio"
                                name="list-visibility"
                                value={option.value}
                                checked={list.visibility === option.value}
                                onChange={() => changeVisibility(option.value)}
                            />
                            <span className={styles.optionBody}>
                                <span className={styles.optionLabel}>{option.label}</span>
                                <span className={styles.optionHint}>{option.hint}</span>
                            </span>
                        </label>
                    ))}
                </fieldset>

                {error && <div className={styles.error} role="alert">{error}</div>}

                {shareUrl ? (
                    <>
                        <div className={styles.linkRow}>
                            <input className={styles.linkInput} value={shareUrl} readOnly onFocus={(e) => e.target.select()} aria-label="Share link" />
                            <button type="button" className={styles.copyBtn} onClick={copyLink}>{copied ? 'Copied!' : 'Copy'}</button>
                        </div>

                        <div className={styles.channels}>
                            <button type="button" className={styles.channel} onClick={nativeShare}>Share…</button>
                            <a className={styles.channel} href={links.sms} onClick={track('sms')}>Text</a>
                            <a className={styles.channel} href={links.whatsapp} target="_blank" rel="noopener noreferrer" onClick={track('whatsapp')}>WhatsApp</a>
                            <a className={styles.channel} href={links.x} target="_blank" rel="noopener noreferrer" onClick={track('x')}>X</a>
                            <a className={styles.channel} href={links.facebook} target="_blank" rel="noopener noreferrer" onClick={track('facebook')}>Facebook</a>
                            <a className={styles.channel} href={links.email} onClick={track('email')}>Email</a>
                        </div>

                        <div className={styles.cardSection}>
                            <div className={styles.cardHead}>
                                <span className={styles.legend}>Share card</span>
                                <a className={styles.cardDownload} href={cardUrl} download={`aluvo-${list.shareSlug}.png`} target="_blank" rel="noopener noreferrer" onClick={track('card_download')}>
                                    Download image
                                </a>
                            </div>
                            <img key={cardKey} src={cardUrl} alt={`${list.name} share card`} className={styles.cardPreview} />
                            <p className={styles.cardHint}>This image is also the preview shown when the link is posted on social media or sent in a text.</p>
                        </div>
                    </>
                ) : (
                    <p className={styles.offHint}>Make this list Public to get a link and a share card. Private lists are only visible to you.</p>
                )}
            </div>
        </div>
    );
}
